import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { unstable_batchedUpdates } from "react-dom";
import { useNavigate, useSearchParams } from "react-router-dom";
import { uniqBy } from "lodash";
import axiosInstant from "../../../../../api";
import { getBrandEmbeds } from "../../../../../api/endpoints";
import { usePortalPath } from "../../../../../hooks/usePortalPath";
import { IEmbed } from "../../../embeds/embedsV2/interface";
import { IconStoryGlyph, PanelEmpty, PanelError, StoryTile } from "../../../videos/selectedVideoCardV2/panels";
import { Shimmer } from "../components/PageStates";
import { SECTION_IDS } from "../components/SectionRail";
import { focusFirstButton, useRetryFocus } from "../components/focusReturn";
import { isSessionRedirect } from "../pageModel";

/**
 * The Stories view of the Videos section, in the video details page's design
 * (selectedVideoCardV2/StoriesList.tsx): StoryTile cards (cover, "N videos"
 * badge, title, experts line), the dashed PanelEmpty / PanelError wells, and a
 * skeleton in the tile geometry. It replaces productDetail/ProductEmbeds.tsx
 * (the old "N Stories" rule + embeds grid, kept untouched for rollback) on
 * this page only.
 *
 * The data is ProductEmbeds' call: the route brand's stories that include
 * this product (getBrandEmbeds with productId), the first STORIES_LIMIT only.
 * Unlike ProductEmbeds, nothing else comes from the URL: its `search` is the
 * product list's (the list keeps every param when it opens a product,
 * productListView/index.tsx:147-149), and matched against story names it
 * turned a search for the product into a false "No stories yet", with no
 * chip to say why. Nothing on this page writes `limit` either. Its list rows
 * carry each story's files with their videoPros (brands.js getBrandEmbeds),
 * which is what the tile's cover and experts line read. A click opens the
 * story, as both old surfaces did.
 *
 * Tiles sit in a grid here (the video page stacks them in a narrow panel).
 */

interface Props {
  /** The route's brand, as ProductEmbeds used it. */
  brandId: number;
  /** The total once loaded, for the section heading's count pill. */
  onCountChange?: (count: number | null) => void;
}

type Status = "loading" | "ready" | "error";

/** ProductEmbeds' default page size (its `limit` param was never set on this page). */
const STORIES_LIMIT = 20;

const GRID = "grid gap-4";
const GRID_STYLE: React.CSSProperties = { gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))" };

/** "Mike Trulove · Dr. Alandra W-B" / "Mike Trulove and 2 others" — copied from StoriesList.tsx. */
const expertLine = (embed: IEmbed): string => {
  const people = uniqBy(
    (embed.files || []).map((file) => file.videoPros?.[0]).filter(Boolean),
    "userid",
  );
  const names = people.map((p) => p?.displayName).filter(Boolean) as string[];
  if (names.length === 0) return "";
  if (names.length <= 2) return names.join(" · ");
  return `${names[0]} and ${names.length - 1} others`;
};

/** The first video's thumbnail — copied from StoriesList.tsx. */
const coverFor = (embed: IEmbed): string | undefined => {
  const first = (embed.files || [])[0];
  if (!first) return undefined;
  const url = first.location || first.reviewS3File?.location || "";
  return url ? url.replace(".mp4", "_thumbnail.png") : undefined;
};

/** StoryTile's geometry (panels/tiles.tsx): p-2.5 card, 96px cover, title + experts lines. */
const TilesSkeleton: FC = () => (
  <div className={GRID} style={GRID_STYLE} aria-hidden="true">
    {[0, 1, 2].map((i) => (
      <div key={i} className="border border-border-token rounded-xl p-2.5 bg-surface">
        <Shimmer className="rounded-lg w-full" style={{ height: 96 }} />
        <Shimmer className="rounded mt-[9px]" style={{ width: "60%", height: 11 }} />
        <Shimmer className="rounded mt-1" style={{ width: "40%", height: 9 }} />
      </div>
    ))}
  </div>
);

export const ProductStories: FC<Props> = ({ brandId, onCountChange }) => {
  const navigate = useNavigate();
  const { p } = usePortalPath();
  const [searchParams] = useSearchParams();
  const productId = searchParams.get("productId") || "";

  const [stories, setStories] = useState<IEmbed[]>([]);
  const [count, setCount] = useState(0);
  const [status, setStatus] = useState<Status>("loading");
  const [attempt, setAttempt] = useState(0);
  // Try again swaps the well for the skeleton, and its button (focused) goes
  // with it: once the list lands, focus goes to what replaced it — Try again,
  // or the first story — only while the reader is still here (focusReturn).
  // Stories is brand-only, where the Videos section draws its tab panel.
  const markRetry = useRetryFocus(status !== "loading", () =>
    focusFirstButton(SECTION_IDS.videos, '[role="tabpanel"] button:not([disabled])'),
  );
  const retry = useCallback(() => {
    markRetry();
    setAttempt((n) => n + 1);
  }, [markRetry]);

  useEffect(() => {
    if (!productId) return undefined;
    let live = true;
    setStatus("loading");
    onCountChange?.(null);
    axiosInstant
      .get(getBrandEmbeds(brandId, STORIES_LIMIT, 1, "", productId))
      .then(({ data }) => {
        if (!live) return;
        const rows: IEmbed[] = data?.rows || [];
        const total: number = data?.count || 0;
        // One render for the list and the heading's count: React 17 doesn't
        // batch updates made in a promise callback (as index.tsx's leave).
        unstable_batchedUpdates(() => {
          setStories(rows);
          setCount(total);
          setStatus("ready");
          onCountChange?.(total);
        });
      })
      // The old view had no catch: a failure left its loader spinning. Now it
      // is an error well with a retry, never an empty "no stories". A session
      // redirect (a 401, the expired-token cancel) is on its way to login:
      // the skeleton stays. The error well shows neither the stories nor the
      // count, and the next load's success replaces both.
      .catch((err) => {
        if (!live || isSessionRedirect(err)) return;
        unstable_batchedUpdates(() => {
          setStatus("error");
          onCountChange?.(null);
        });
      });
    return () => {
      live = false;
    };
    // onCountChange is a stable setter from the section, so it isn't a dep.
  }, [brandId, productId, attempt]);

  const tiles = useMemo(
    () =>
      stories.map((embed) => ({
        id: embed.id,
        title: embed.name,
        cover: coverFor(embed),
        subtitle: expertLine(embed),
        videoCount: (embed.files || []).length,
      })),
    [stories],
  );

  if (status === "loading") return <TilesSkeleton />;
  if (status === "error") {
    // Announced, and Try again 44px below md, as the page's other load errors
    // (role="alert", TouchTarget). PanelError is the video page's, unedited;
    // TouchTarget only reaches a direct child button, so the size is set here.
    return (
      <div role="alert" className="max-md:[&_button]:h-11">
        <PanelError
          icon={<IconStoryGlyph size={24} strokeWidth={1.6} />}
          title="Couldn't load stories"
          onRetry={retry}
          minHeight={140}
        />
      </div>
    );
  }
  if (tiles.length === 0) {
    return (
      <PanelEmpty
        icon={<IconStoryGlyph size={24} strokeWidth={1.6} />}
        title="No stories yet"
        body="Stories that include videos of this product will appear here."
        minHeight={140}
      />
    );
  }
  return (
    <div className="flex flex-col gap-3">
      <div className={GRID} style={GRID_STYLE}>
        {tiles.map((tile) => (
          <StoryTile
            key={tile.id}
            cover={tile.cover}
            title={tile.title}
            subtitle={tile.subtitle}
            videoCount={tile.videoCount}
            onClick={() => navigate(p(`/videos/embeds?embed=${tile.id}`))}
          />
        ))}
      </div>
      {count > tiles.length && (
        <p className="m-0 text-center text-text-2" style={{ fontSize: 12 }}>
          Showing {tiles.length} of {count}
        </p>
      )}
    </div>
  );
};
